import type { I18nKey } from "@/core/i18n";
import { Button } from "@/modules/shadcn/ui/button";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { validateCameraApiUrl } from "../../utils/validateCameraApiUrl";
import { useCreateProjectMutation } from "../../services/projectApi";
import { Modal } from "../Modal";
import { ProjectDetailsForm } from "../ProjectDetailsForm";
import { apiErrorMessage } from "@/core/api";

interface CreateProjectModalProps {
  onClose: () => void;
  initialName?: string;
}

export const CreateProjectModal = ({
  onClose,
  initialName,
}: CreateProjectModalProps) => {
  const { t } = useTranslation(["project", "common"]);
  const [name, setName] = useState(initialName ?? "");
  const [description, setDescription] = useState("");
  const [deviceId, setDeviceId] = useState<number | null>(null);
  const [cameraApiUrl, setCameraApiUrl] = useState<string | null>(null);
  const [cameraApiUrlError, setCameraApiUrlError] =
    useState<I18nKey<"project"> | null>(null);
  const [selectedCameraMxid, setSelectedCameraMxid] = useState<string | null>(null);

  const [createProject, { isLoading: isCreatingProject }] =
    useCreateProjectMutation();

  const handleCameraApiUrlChange = (val: string) => {
    setCameraApiUrl(val);
    if (cameraApiUrlError) setCameraApiUrlError(validateCameraApiUrl(val));
  };

  const handleCameraApiUrlBlur = () => {
    setCameraApiUrlError(validateCameraApiUrl(cameraApiUrl));
  };

  /**
   * A device and a local address are alternatives, not a pair — see the same
   * handler in EditProjectModal for why saving both was wrong.
   */
  const handleDeviceChange = (next: number | null) => {
    if (next === deviceId) {
      return;
    }

    setDeviceId(next);
    setSelectedCameraMxid(null);

    // The address is dropped by the save, not here: clearing the field would
    // lose what the user typed if they switch back to the local network, and
    // the field is hidden while a device is selected anyway. Its *error* does
    // have to go — it disables Save, and a stale address must not block the
    // save that replaces it with a device.
    if (next !== null) {
      setCameraApiUrlError(null);
    }
  };

  const handleSave = async () => {
    if (!name.trim()) return;

    // With a device chosen there is no address to validate — it is not stored.
    if (deviceId === null) {
      const urlError = validateCameraApiUrl(cameraApiUrl);
      if (urlError) {
        setCameraApiUrlError(urlError);
        return;
      }
    }

    const normalizedUrl =
      deviceId !== null ? null : (cameraApiUrl ?? "").trim() || null;

    try {
      // Labels are not part of creating a project any more — they are set up on
      // the Annotate page, where the images they describe are.
      await createProject({
        name,
        description,
        deviceId,
        cameraApiUrl: normalizedUrl,
        cameraMxid: selectedCameraMxid,
      }).unwrap();

      onClose();
    } catch (error) {
      console.error("Project creation failed:", error);
      toast.error(apiErrorMessage(error, t("createModal.failed")));
    }
  };

  const buttons = (
    <div className="flex flex-row gap-3">
      <Button
        variant="destructive"
        size="sm"
        onClick={onClose}
        disabled={isCreatingProject}
      >
        {t("common:actions.delete")}
      </Button>
      <Button
        size="sm"
        onClick={handleSave}
        disabled={isCreatingProject || !name.trim() || !!cameraApiUrlError}
      >
        {isCreatingProject ? t("saving") : t("common:actions.save")}
      </Button>
    </div>
  );

  return (
    <Modal
      title={t("createModal.title")}
      closeButton={false}
      buttons={buttons}
      onClose={onClose}
    >
      <ProjectDetailsForm
        name={name}
        setName={setName}
        description={description}
        setDescription={setDescription}
        deviceId={deviceId}
        setDeviceId={handleDeviceChange}
        cameraApiUrl={cameraApiUrl}
        setCameraApiUrl={handleCameraApiUrlChange}
        cameraApiUrlError={cameraApiUrlError}
        onCameraApiUrlBlur={handleCameraApiUrlBlur}
        selectedCameraMxid={selectedCameraMxid}
        setSelectedCameraMxid={setSelectedCameraMxid}
      />
    </Modal>
  );
};
